import { ISession } from '../../models/session';
import { Injectable } from '@angular/core';
import { Response } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { BasicService } from '../../providers/basic';
import { environment as config } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { AuthHttp } from 'angular2-jwt';
import { AuthService } from '../auth/auth.service';
import { FormBuilder, Validators, FormControl } from '@angular/forms';
import { ITag } from '../../models/tag';
import { ShortUrlService } from './shorturl.service';
import { empty, merge, toPairs, fromPairs, chain, map, omit, pick, prop } from 'ramda/es';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import * as store from 'store';
import { ThemeDirective } from '../../theme/theme.directive';
import { ThemeService } from '../../theme/theme.service';
import { eva_constants } from '../../eva_constants';
import { ICategory } from '../../models/category';

@Injectable()
export class SessionsService extends BasicService {
  private API = config.sessions.url;

  public isPremium$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public guestMode$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public sessionForm = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
    date: ['', Validators.required],
    expirationdatetime: ['', Validators.required],
    description: ['', Validators.minLength(5)],
    shortUrl: [''],
    guestMode: new FormControl(false),
    categories: Array<ICategory>()
  });
  private updateFields = [
    'id',
    'speaker',
    'name',
    'date',
    'expirationdatetime',
    'description',
    'shortUrl',
    'shortId',
    'isPremium',
    'status',
    'eurostudioStatus',
    'url',
    'types',
    'phoneNumber',
    'guestMode',
    'maxusers',
    'language',
    'topics'
  ];

  constructor(
    protected authService: AuthService,
    protected translate: TranslateService,
    private http: AuthHttp,
    private shortUrlService: ShortUrlService,
    private fb: FormBuilder
  ) {
    super(authService, translate);
  }

  private removeDelimiter(sessionUrl = '') {
    let delimiter = 'livee.io/';
    let index = sessionUrl.indexOf(delimiter);
    sessionUrl = index > 0 ? sessionUrl.slice(index + delimiter.length) : sessionUrl;

    delimiter = 'mylive.tv/';
    index = sessionUrl.indexOf(delimiter);

    return index > 0 ? sessionUrl.slice(index + delimiter.length) : sessionUrl;
  }

  private removeSessionUrlDelimiter(session) {
    session.shortUrl = this.removeDelimiter(session.shortUrl);
    return session;
  }

  public fixLinkForStaging(link) {
    return link.replace(/eva.livee/g, 'eva.staging.livee');
  }

  public fixLinkForMyLiveTV(link) {
    return link
      .replace(/eva.livee.io/g, 'mylive.tv')
      .replace(/eva.staging.livee.io/g, 'staging.mylive.tv')
      .replace(/eva.livee.com/g, 'admin.mylive.tv')
      .replace(/eva.staging.livee.com/g, 'admin.staging.mylive.tv');
  }

  private fixLinksForEnvironment(session) {
    if (config.staging) {
      if (session.url) {
        session.url = this.fixLinkForStaging(session.url);
      }
      if (session.urls) {
        session.urls.forEach(url => {
          if (url && url.link) {
            url.link = this.fixLinkForStaging(url.link);
          }
        });
      }
    }

    const theme = store.get(ThemeDirective.THEME);
    if (theme && theme === eva_constants.MYLIVETV_THEME) {
      if (session.url) {
        session.url = this.fixLinkForMyLiveTV(session.url);
      }
      if (session.urls) {
        session.urls.forEach(url => {
          if (url && url.link) {
            url.link = this.fixLinkForMyLiveTV(url.link);
          }
        });
      }
    }

    return session;
  }

  public getExports(sessionId: string) {
    const prefixUrl = `${config.stats.url}/${this.authService.user.name}/sessions/${sessionId}`;
    return [
      {
        url: `${prefixUrl}/statistic/exports`,
        name: 'sessions.statistic.export'
      },
      {
        url: `${prefixUrl}/registered_users/exports`,
        name: 'sessions.statistic.export_users'
      },
      {
        url: `${prefixUrl}/params/exports`,
        name: 'sessions.statistic.export_params'
      },
      {
        url: `${prefixUrl}/max_users/exports`,
        name: 'sessions.statistic.export_max_users'
      },
      {
        url: `${prefixUrl}/before_live_questions/exports`,
        name: 'sessions.statistic.export_before_live_questions'
      },
      {
        url: `${prefixUrl}/questions/exports`,
        name: 'sessions.statistic.export_live_questions'
      }
    ];
  }

  public getModels(notify: boolean = false) {
    return this.http
      .get(`${this.API}/sessionmodel`)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleError(err));
  }

  public getAll(notify: boolean = false) {
    return this.http
      .get(`${this.API}/sessions`)
      .map(res => this.extractData(res, notify))
      .map(sessions => sessions.filter(session => session.name.toLowerCase() !== 'demo'))
      .catch(err => this.handleError(err));
  }

  public getOne(id: string, notify: boolean = false) {
    return this.http
      .get(`${this.API}/sessions/${id}`)
      .map(res => this.extractData(res, notify))
      .map(session => this.removeSessionUrlDelimiter(session))
      .map(session => this.fixLinksForEnvironment(session))
      .do(session => this.isPremium$.next(session.isPremium))
      .do(session => this.guestMode$.next(session.guestMode))
      .catch(err => this.handleError(err));
  }

  public create(data, notify: boolean = true) {
    return this.http
      .post(`${this.API}/sessions`, data)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleError(err));
  }

  public duplicate(id: string, data, notify: boolean = true) {
    return this.http
      .post(`${this.API}/sessions/${id}`, data)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleError(err));
  }

  public update(id: string, session, notify: boolean = true) {
    const data = pick(this.updateFields, session);
    const payload = { from: [this.removeDelimiter(data.shortUrl)], to: data.url };
    // @TODO: update phone booking if date changed
    return this.shortUrlService
      .update(data.shortId, payload)
      .mergeMap(link => {
        if (link) {
          data.shortId = link.id;
        }

        const theme = store.get(ThemeDirective.THEME);
        const isMyLiveTV = theme && theme === eva_constants.MYLIVETV_THEME;
        const shortBaseUrl = isMyLiveTV
          ? this.fixLinkForMyLiveTV(config.livee.short)
          : config.livee.short;
        if (data.shortUrl) {
          data.shortUrl = `${shortBaseUrl}${data.shortUrl}`;
        }
        return this.http
          .put(`${this.API}/sessions/${id}`, omit(['url'], data))
          .map(res => {
            // keep current session url, because php api after update return wrong one
            const updatedSession = merge(this.extractData(res, notify), { url: data.url });
            if (!link) {
              return this.removeSessionUrlDelimiter(updatedSession);
            }
            return merge(updatedSession, { shortUrl: payload.from[0] });
          })
          .do(() => {
            this.isPremium$.next(session.isPremium);
            this.guestMode$.next(session.guestMode);
          });
      })
      .catch(err => this.handleError(err));
  }

  public deleteById(id: string, notify: boolean = true) {
    return this.http
      .get(`${this.API}/sessions/${id}`)
      .map(res => res.json())
      .mergeMap(session =>
        this.shortUrlService
          .delete(session.shortId)
          .mergeMap(link =>
            this.http.delete(`${this.API}/sessions/${id}`).map(res =>
              this.extractData(res, notify, {
                success: 'sessions.remove.session_removed'
              })
            )
          )
          .catch(err => this.handleError(err))
      );
  }

  public updateStatus(id, status) {
    return this.http.put(`${this.API}/sessions/${id}/status`, { status });
  }

  public reset(id) {
    return this.http.put(`${this.API}/sessions/${id}/reset`, {});
  }

  public publish(session: ISession) {
    return this.http
      .put(`${this.API}/sessions/${session.id}/publish`, {})
      .catch(err => this.handleError(err));
  }

  public close(session: ISession, data: any) {
    return this.http
      .put(`${this.API}/sessions/${session.id}/close`, {})
      .map(response => response.json())
      .catch(err => this.handleError(err));
  }

  public setAudience(session: ISession, audience: number) {
    const ctx = store.get(`${session.id}_ctx`);
    return store.set(`${session.id}_ctx`, merge(ctx, {'audience': audience}));
  }

  public getAudience(session: ISession) {
    const ctx = store.get(`${session.id}_ctx`);
    return prop('audience')(ctx);
  }

  public setComplete(session: ISession, module: string) {
    const ctx = store.get(`${session.id}_ctx`);
    store.set(`${session.id}_ctx`, merge(ctx, {[module]: true}));
  }

  public isComplete(session: ISession, module: string) {
    const ctx = store.get(`${session.id}_ctx`);
    return !!prop(module)(ctx);
  }

  public getAllCategories() {
    return this.http
      .get(`${this.API}/categories`, {})
      .map(response => response.json())
      .catch(err => this.handleError(err));
  }

  public getCategory(id: string) {
    return this.http
      .get(`${this.API}/categories/${id}`, {})
      .map(response => response.json())
      .catch(err => this.handleError(err));
  }

  public updateCategory(category: ICategory) {
    return this.http
      .put(`${this.API}/categories/${category.id}`, {name: category.name})
      .map(response => response.json())
      .catch(err => this.handleError(err));
  }

  public addCategory(name: string) {
    return this.http
      .post(`${this.API}/categories`, { name })
      .map(response => response.json())
      .catch(err => this.handleError(err));
  }

  public deleteCategory(id: string) {
    return this.http
      .delete(`${this.API}/categories/${id}`)
      .map(response => response.json())
      .catch(err => this.handleError(err));
  }

  public getAllSessionCategories(session: ISession) {
    return this.http
      .get(`${this.API}/sessions/${session.id}/categories`, {})
      .map(response => response.json())
      .catch(err => this.handleError(err));
  }

  public updateSessionCategories(session: ISession, ids: Array<string>) {
    return this.http
      .put(`${this.API}/sessions/${session.id}/categories`, {categoryIds: ids})
      .map(response => response.json())
      .catch(err => this.handleError(err));
  }

}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/sessions.service.ts